import { ReactNode } from 'react';
import {
  Dialog,
  DialogProps,
  Heading,
  Modal,
  ModalOverlay,
  ModalOverlayProps,
} from 'react-aria-components';
import { cx } from '../util';
import { ClarionButton } from './ClarionButton';
import Divider from './Divider';

type ClarionDialogProps = DialogProps & {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: ReactNode;
  isDismissable?: boolean;
  showCloseButton?: boolean;
};

export function ClarionDialog({
  className,
  children,
  title,
  isOpen,
  onClose,
  isDismissable = true,
  style,
  showCloseButton = false,
  ...props
}: ClarionDialogProps) {
  return (
    <ClarionModal
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => !newIsOpen && onClose?.()}
      isDismissable={isDismissable}
      style={style}
    >
      <Dialog
        {...props}
        className={cx('outline-0 p-5 pt-4 max-h-[inherit] relative bg-inherit', className)}
      >
        <div className="flex justify-between items-start">
          {title && (
            <Heading slot="title" className="text-2xl font-bold mb-4">
              {title}
            </Heading>
          )}
          {showCloseButton && (
            <ClarionButton
              icon="cross"
              variant="minimal"
              size="large"
              onClick={onClose}
              className="-mr-2 -mt-1"
              aria-label="Close"
            />
          )}
        </div>
        {children}
      </Dialog>
    </ClarionModal>
  );
}

type ClarionDialogFooterProps = {
  className?: string;
  children: ReactNode;
};

// Sticky footer that right-aligns action buttons
export function ClarionDialogFooter({ className, children }: ClarionDialogFooterProps) {
  return (
    <div className={`sticky bottom-0 my-0 bg-gray-50 dark:bg-neutral-800 mt-5`}>
      <Divider className="mb-5" />
      <div className={cx('flex justify-end gap-2 items-center pb-5 -mb-5', className)}>
        {children}
      </div>
    </div>
  );
}

// Internal modal backdrop and container
function ClarionModal({ style, ...props }: ModalOverlayProps) {
  return (
    <ModalOverlay
      {...props}
      className="fixed inset-0 z-20 bg-black/[35%] grid place-items-center motion-safe:animate-fade-in ease-out"
    >
      <Modal
        {...props}
        style={style}
        className="max-w-2xl max-h-full rounded-xl bg-gray-50 dark:bg-neutral-800 shadow-2xl bg-clip-padding border border-black/10 dark:border-white/30 motion-safe:animate-slide-in overflow-auto relative forced-colors:border-2"
      />
    </ModalOverlay>
  );
}
